import { createPullPlan, setLeafTakt } from "../../../../api/clientV2";
import type { PullPlanKind } from "../../../../models";
import { makeId } from "../../../../utils/id";

export interface CreateFromWbsArgs {
  kind: PullPlanKind;
  /** Checked WBS-leaf ids, in check order; the first anchors the draft. */
  leafIds: number[];
  /** Display/section name for the plan. */
  name: string;
  /** Optional human-readable name per leaf, used to seed Takt zone rows. */
  leafNameById?: Map<number, string>;
}

export interface CreateFromWbsResult {
  sectionName: string;
  draftId: number;
  anchorWbsId: number;
  kind: PullPlanKind;
}

/** Create a new pull plan from a set of WBS leaves.
 *
 *  kind="takt": a Takt section. The draft carries `takt_section_name` so it
 *  shows on the board and as a section card; the payload seeds one zone row per
 *  leaf. After the draft exists, each leaf is stamped with Section/A via
 *  setLeafTakt(apply_to_all:false) so ALREADY-tagged work is left alone — only
 *  the leaf's untagged activities join this section.
 *
 *  kind="plain": a non-Takt plan. No `takt_section_name` (never on the board),
 *  anchored on the first leaf's WBS id, and NO Takt stamping. */
export async function createFromWbs(
  scheduleId: number,
  args: CreateFromWbsArgs,
): Promise<CreateFromWbsResult> {
  const { kind, leafIds, name, leafNameById } = args;
  const trimmedName = name.trim();
  const anchorWbsId = leafIds[0];
  const today = new Date().toLocaleDateString(undefined, {
    month: "numeric",
    day: "numeric",
  });
  const planName = `${trimmedName} — ${today}`;

  if (kind === "plain") {
    const created = await createPullPlan(scheduleId, {
      section_wbs_id: anchorWbsId,
      name: planName,
      scope_wbs_ids: leafIds,
      payload_json: JSON.stringify({
        kind: "plain",
        scope_wbs_ids: leafIds,
        rows: [],
      }),
    });
    return { sectionName: trimmedName, draftId: created.id, anchorWbsId, kind };
  }

  // Takt: seed one board row per pulled leaf (zone = the leaf's name) so the
  // workspace opens with a row to pull each leaf's work onto. Geometry only —
  // no staged ops, so an immediate discard stays clean.
  const rows = leafIds.map((id) => ({
    rowId: makeId("row"),
    takt_a: "",
    takt_b: leafNameById?.get(id) ?? "",
    takt_c: "",
  }));
  const created = await createPullPlan(scheduleId, {
    section_wbs_id: anchorWbsId,
    name: planName,
    takt_section_name: trimmedName,
    scope_wbs_ids: leafIds,
    payload_json: JSON.stringify({
      kind: "takt",
      scope_wbs_ids: leafIds,
      rows,
      instances: [],
    }),
  });

  // Stamp Section/A onto each leaf's UNTAGGED activities only — apply_to_all
  // false so work already tagged to another section is never clobbered.
  for (const id of leafIds) {
    await setLeafTakt(scheduleId, id, {
      section_name: trimmedName,
      takt_a: trimmedName,
      apply_to_all: false,
    });
  }

  return { sectionName: trimmedName, draftId: created.id, anchorWbsId, kind };
}
